/**
 * Columns and rows of the line-item table.
 *
 * One row per sample and line item. The attributes are not a column of their own: each row carries the result
 * of every attribute mapped to its sample ("Result by attribute"). Evidence is attached to the row of the sample
 * it is mapped to, not to the whole table: only the files mapped to that sample are listed, so there are no
 * empty columns for other samples' files.
 *
 * Nothing here is specific to one control: the line-item columns come from the SOURCE file's own columns (a
 * spreadsheet's headers, a statement's date / description / amount).
 */
import { formatMoney } from '../../shared/format/money.ts';
import { itemMatchPlaces, isRealFile } from './itemFields';

const isMatch = (m) => m && (m.status === 'matched' || m.status === 'combined');
const MAX_DEFAULT_LINE_COLS = 4;     // line-item columns shown before the reviewer opens the menu
const MAX_EXTRA_FIELDS = 24;
const STATUS_RANK = { failed: 3, warning: 2, passed: 1 };

const titleCase = (s) => String(s).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
const normKey = (s) => String(s ?? '').replace(/^["']+|["']+$/g, '').trim().toLowerCase().replace(/\s+/g, ' ');
const sameFile = (a, b) => normKey(a).replace(/\.[a-z0-9]+$/, '') === normKey(b).replace(/\.[a-z0-9]+$/, '');

/** Money as printed in a statement: 13376.00 -> 13,376.00. Leaves anything else untouched. */
export function formatAmount(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);
}

/** "p1, 2" / "row 4" for one match entry (or a derived one for runs stored before matches existed). */
export function matchLocation(match) {
  const locs = match?.locations || [];
  const pages = [...new Set(locs.map((l) => l.page_number).filter((p) => p != null))].sort((a, b) => a - b);
  const rows = [...new Set(locs.filter((l) => l.page_number == null).map((l) => l.row_number).filter((r) => r != null))].sort((a, b) => a - b);
  const bits = [];
  if (pages.length) bits.push(`p${pages.join(', ')}`);
  if (rows.length) bits.push(`row ${rows.join(', ')}`);
  return bits.join(' ');
}

/** The match of `item` in `file`: the stored entry, else one derived from matched_to / citation. */
export function matchFor(item, file) {
  const stored = (item?.matches || []).find((m) => sameFile(m.file, file));
  if (stored) return stored;
  const place = itemMatchPlaces(item).find((p) => sameFile(p.file, file));
  if (!place) return null;
  return {
    file: place.file, status: place.combined ? 'combined' : 'matched',
    locations: [...place.pages.map((p) => ({ page_number: p })), ...place.rows.map((r) => ({ row_number: r }))],
    found_amount: item?.source?.amount ?? null, found_parts: [], fields: {},
  };
}

// ── which attributes and which evidence belong to a sample ─────────────────────

const mapped = (c) => c && c.source !== 'missing' && (c.file_name || c.attribute_ref);

/** Evidence-map cells of one sample (spelling-insensitive). */
function cellsOf(evidenceMap, sampleRef) {
  const key = normKey(sampleRef);
  return (evidenceMap || []).filter((c) => mapped(c) && normKey(c.sample_ref) === key);
}

/** The files mapped to a sample: the auditor's own mapping wins; the AI's suggestions only when there is none. */
export function mappedFiles(evidenceMap, sampleRef) {
  const cells = cellsOf(evidenceMap, sampleRef).filter((c) => isRealFile(c.file_name));
  const manual = cells.filter((c) => c.source === 'manual');
  const pick = manual.length ? manual : cells;
  const out = [];
  pick.forEach((c) => { if (!out.some((f) => sameFile(f, c.file_name))) out.push(c.file_name); });
  return out;
}

/** The attributes mapped to a sample, in the table's attribute order: those with a result for it (skipped ones
 *  have no evidence, so they are not mapped) plus any the evidence map names. */
function attrsOf(attrCols, data, evidenceMap, sampleRef) {
  const fromMap = new Set(cellsOf(evidenceMap, sampleRef).map((c) => c.attribute_ref).filter(Boolean));
  return attrCols.filter((a) => {
    const cell = data[a.ref];
    return (cell && cell.status !== 'skipped') || fromMap.has(a.ref);
  });
}

const sourceKey = (item) => {
  const loc = item?.source?.location;
  return loc?.file ? [loc.file, loc.sheet_name, loc.row_number ?? loc.page_number, item.source.ref].join('|') : `ref|${normKey(item?.item_ref)}`;
};

const worst = (statuses) => statuses.filter(Boolean).sort((a, b) => (STATUS_RANK[b] || 0) - (STATUS_RANK[a] || 0))[0];

/**
 * @param rows        [[sampleRef, data]] as pivoted by SampleDataTable (data[attrRef].items = line items)
 * @param attrCols    the attribute columns being shown: [{ref, label}]
 * @param evidenceMap the run's evidence map cells {sample_ref, attribute_ref, file_name, source}
 * @returns { lineCols, lines, hasItems }
 */
export function buildLineItemModel(rows, attrCols, evidenceMap = []) {
  const lines = [];
  const allItems = [];
  rows.forEach(([ref, data], sampleIndex) => {
    const attrs = attrsOf(attrCols, data, evidenceMap, ref);
    const files = mappedFiles(evidenceMap, ref);
    const groups = new Map();
    attrs.forEach((attr) => {
      (data[attr.ref]?.items || []).forEach((item) => {
        const k = sourceKey(item);
        if (!groups.has(k)) groups.set(k, { item, perAttr: new Map() });
        groups.get(k).perAttr.set(attr.ref, item);
      });
    });
    const results = (g) => attrs.map((attr) => {
      const cell = data[attr.ref];
      const item = g?.perAttr.get(attr.ref) || null;
      // A check that never completed (timed out or failed) has no verdict of its own: it is shown as such.
      if (cell?.incomplete) return { attr, cell, item, status: 'incomplete' };
      return { attr, cell, item, status: item ? item.status : cell?.status };
    });
    if (!groups.size) {
      lines.push({ sampleRef: ref, sampleIndex, data, item: null, number: null, first: true, results: results(null), files, attrs });
      return;
    }
    let n = 0;
    groups.forEach((g) => {
      n += 1;
      const items = [...g.perAttr.values()];
      allItems.push(g.item);
      // The viewer and the evidence cell see this line as one item: every attribute's matches, worst status.
      const matches = [];
      items.forEach((it) => (it.matches || []).forEach((m) => { if (!matches.some((x) => x.file === m.file && x.status === m.status && matchLocation(x) === matchLocation(m))) matches.push(m); }));
      const merged = { ...g.item, matches, status: worst(items.map((i) => i.status)) || g.item.status };
      lines.push({ sampleRef: ref, sampleIndex, data, item: merged, number: n, first: n === 1, results: results(g), files, attrs, sources: items });
    });
  });

  const perSample = new Map();
  lines.forEach((l) => perSample.set(l.sampleRef, (perSample.get(l.sampleRef) || 0) + 1));
  lines.forEach((l) => { l.siblings = perSample.get(l.sampleRef) || 1; });          // line items of the same sample

  const c = collectSourceColumns(allItems);
  const lineCols = [{ id: 'line:ref', label: 'Line item', defaultVisible: true, kind: 'ref' }];
  const optional = [];
  if (c.has.reference && c.referenceDiffers) optional.push({ id: 'line:reference', label: 'Reference', key: 'reference', defaultVisible: true });
  if (c.has.amount) optional.push({ id: 'line:amount', label: 'Amount', key: 'amount', defaultVisible: true, money: true });
  if (c.has.date) optional.push({ id: 'line:date', label: 'Date', key: 'date', defaultVisible: !c.ledgerOnly });
  if (c.has.party) optional.push({ id: 'line:party', label: 'Description', key: 'party', defaultVisible: !c.ledgerOnly });
  c.sourceFields.slice(0, MAX_EXTRA_FIELDS).forEach((h) => optional.push({ id: `line:f:${h}`, label: titleCase(h), field: h, defaultVisible: false }));
  let shown = 1;
  optional.forEach((col) => {
    if (col.defaultVisible && shown >= MAX_DEFAULT_LINE_COLS) col.defaultVisible = false;
    if (col.defaultVisible) shown += 1;
    lineCols.push({ ...col, kind: col.field ? 'field' : 'source' });
  });
  return { lineCols, lines, hasItems: allItems.length > 0 };
}

function collectSourceColumns(items) {
  const sourceFields = new Map();
  let ledgerOnly = true;
  let referenceDiffers = false;
  const has = { date: false, party: false, amount: false, reference: false };
  items.forEach((it) => {
    const src = it.source;
    if (!src) return;
    if (src.origin !== 'ledger') ledgerOnly = false;
    ['date', 'party', 'amount', 'reference'].forEach((k) => { if (src[k]) has[k] = true; });
    if (src.reference && src.reference !== it.item_ref) referenceDiffers = true;
    Object.keys(src.fields || {}).forEach((h) => sourceFields.set(h, (sourceFields.get(h) || 0) + 1));
  });
  return { ledgerOnly, has, referenceDiffers, sourceFields: [...sourceFields.entries()].sort((a, b) => b[1] - a[1]).map(([h]) => h) };
}

/** Plain-text value of a line-item column for one item (the component adds layout around it). */
export function lineCellValue(col, item, currency = null) {
  const src = item?.source;
  if (col.kind === 'ref') return item?.item_ref || '';
  if (col.kind === 'field') return src?.fields?.[col.field] || '';
  const v = src?.[col.key];
  return col.money ? formatMoney(v, currency, { empty: '' }) : (v || '');
}

/**
 * What the evidence cell of a line lists: the files mapped to the line's sample, each with what is known about
 * the line in it. Runs with no evidence map fall back to the files the item matched.
 * entry: { file, text, tone, sub }
 */
export function evidenceEntries(line, currency = null) {
  const money = (v) => formatMoney(v, currency, { empty: '' });
  const item = line.item;
  const files = line.files?.length
    ? line.files
    : [...new Set(itemMatchPlaces(item).map((p) => p.file).concat((item?.matches || []).map((m) => m.file)).filter(isRealFile))];
  const amount = item?.source?.amount ? money(item.source.amount) : '';
  // The file a line comes from (the statement, the population) is not evidence for itself.
  const own = item?.source?.location?.file;
  return files.filter((file) => !(own && sameFile(file, own))).map((file) => {
    const m = item ? matchFor(item, file) : null;
    if (!m) return { file, text: 'read by AI', tone: 'muted' };
    if (isMatch(m)) {
      const found = money(m.found_amount ?? item?.source?.amount);
      const parts = m.found_parts?.length ? m.found_parts.map(money).join(' + ') : '';
      return { file, text: [matchLocation(m), found].filter(Boolean).join(' · '), tone: 'ok', sub: parts ? `${parts} (two amounts add up to it)` : undefined };
    }
    if (m.status === 'inconclusive') return { file, text: 'could not be read reliably', tone: 'warn', sub: m.note };
    return { file, text: amount ? `${amount} is not on this document` : 'not on this document', tone: 'muted' };
  });
}
